import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, DragIndicator } from '@mui/icons-material';
import { useCollection } from '../hooks/useFirestore';
import { query as fbQuery, orderBy as fbOrderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { deleteDoc, doc, updateDoc } from 'firebase/firestore';

interface FAQ {
  id: string;
  question: string;
  answer?: string;
  blocks?: any[];
  // legacy
  target?: 'school' | 'student' | 'both' | 'lp';
  // new
  targets?: string[];
  order: number;
  status: 'visible' | 'hidden' | 'scheduled';
  publishedAt?: Date;
  createdAt: Date;
}

const TARGET_LABELS: Record<string, string> = {
  school: '教室',
  student: '生徒',
  lp: 'LP',
  faqList: 'FAQ一覧',
};

function uniq(arr: string[]): string[] {
  return Array.from(new Set(arr.filter(Boolean)));
}

function resolveTargets(faq: any): string[] {
  const v = faq?.targets;
  if (Array.isArray(v)) return uniq(v.map(String));
  const legacy = String(faq?.target || '');
  if (!legacy) return [];
  if (legacy === 'both') return ['school', 'student'];
  return [legacy];
}

export default function FAQsPage() {
  const navigate = useNavigate();
  const { data: faqs } = useCollection<FAQ>(
    'faqs',
    (q) => fbQuery(q, fbOrderBy('order', 'asc'))
  );


  const handleDelete = async (faqId: string) => {
    if (!window.confirm('このFAQを削除してもよろしいですか？')) return;

    try {
      await deleteDoc(doc(db, 'faqs', faqId));
    } catch (error) {
      console.error('Failed to delete FAQ:', error);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">FAQ管理</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/faqs/new')}
        >
          新規作成
        </Button>
      </Box>

      <Card>
        <List>
          {faqs?.map((faq, idx) => (
            <ListItem
              key={faq.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', String(idx));
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={async (e) => {
                e.preventDefault();
                const fromIndex = Number(e.dataTransfer.getData('text/plain'));
                const toIndex = idx;
                if (Number.isNaN(fromIndex) || fromIndex === toIndex || !faqs) return;

                // 新しい順序を作成
                const reordered = [...faqs];
                const [moved] = reordered.splice(fromIndex, 1);
                reordered.splice(toIndex, 0, moved);

                // orderを1から採番してバルク更新
                try {
                  const updates = reordered.map((item, i) => updateDoc(doc(db, 'faqs', item.id), { order: i + 1 }));
                  await Promise.all(updates);
                } catch (err) {
                  console.error('Failed to reorder FAQs:', err);
                }
              }}
              secondaryAction={
                <Box>
                  <IconButton
                    edge="start"
                    sx={{ cursor: 'grab', '&:hover': { cursor: 'grab' }, '&:active': { cursor: 'grabbing' } }}
                  >
                    <DragIndicator />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => navigate(`/faqs/${faq.id}/edit`)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDelete(faq.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              }
            >
              <ListItemText
                primary={faq.question}
                secondary={`表示対象: ${(() => {
                  const t = resolveTargets(faq);
                  if (!t || t.length === 0) return '未設定';
                  return t.map((x) => TARGET_LABELS[x] || x).join(' / ');
                })()} / ステータス: ${((faq as any).status || 'visible') === 'visible' ? '表示' : ((faq as any).status || 'visible') === 'hidden' ? '非表示' : '予約'}${(faq as any).publishedAt ? ` / 公開: ${(() => { const v: any = (faq as any).publishedAt; try { const d = typeof v.toDate === 'function' ? v.toDate() : new Date(v); return d.toLocaleString(); } catch { return ''; } })()}` : ''}`}
              />
            </ListItem>
          ))}
        </List>
      </Card>
    </Box>
  );
}